import { useMutation } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { useContext } from 'react';
import logoutMutation from 'src/api/auth/logout';
import AuthContext from 'src/context/AuthContext';
import viewStateVar, { initialViewState } from 'src/store/viewState';

export const TOKEN = 'token';
export const REFRESH_EXPIRES_IN = 'refreshExpiresIn';
export const TOKEN_EXPIRES = 'tokenExpires';
const MEDIA_LAST_POSITION = 'MEDIA_LAST_POSITION';

interface TokenData {
  [key: string]: string | null;
}

export const useLogout = (callback?: () => void): (() => void) => {
  const [logOutMutation] = useMutation(logoutMutation);
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  return () => {
    logOutMutation().finally(() => {
      typeof callback === 'function' && callback();
      removeTokenAndExpire();
      removeLastMediaPositions();
      // if this hook is called within the AuthContext, set hasToken in App to false
      authContext?.setHasToken?.(false);
      viewStateVar(initialViewState);
      navigate({ to: '/' });
    });
  };
};

export function getTokenAndExpire(): TokenData {
  return {
    [TOKEN]: localStorage.getItem(TOKEN) || null,
    [REFRESH_EXPIRES_IN]: localStorage.getItem(REFRESH_EXPIRES_IN) || null,
    [TOKEN_EXPIRES]: localStorage.getItem(TOKEN_EXPIRES) || null,
  };
}

export function getHFExpirationData(): Record<string, string> {
  const format = 'MM:DD:YYYY hh:mm:ss';
  return {
    now: moment().format(format),
    [REFRESH_EXPIRES_IN]: moment(Number(localStorage.getItem(REFRESH_EXPIRES_IN)) * 1000).format(format),
    [TOKEN_EXPIRES]: moment(Number(localStorage.getItem(TOKEN_EXPIRES)) * 1000).format(format),
  };
}

export function setTokenAndExpire(
  token: string,
  refreshExpiresIn: string | number,
  tokenExpires: string | number,
): void {
  localStorage.setItem(TOKEN, token);
  localStorage.setItem(REFRESH_EXPIRES_IN, String(refreshExpiresIn));
  localStorage.setItem(TOKEN_EXPIRES, String(tokenExpires));
}

export function removeTokenAndExpire(): void {
  localStorage.removeItem(TOKEN);
  localStorage.removeItem(REFRESH_EXPIRES_IN);
  localStorage.removeItem(TOKEN_EXPIRES);
}

function removeLastMediaPositions(): void {
  const arr: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.substring(0, MEDIA_LAST_POSITION.length) === MEDIA_LAST_POSITION) {
      arr.push(key);
    }
  }
  for (let j = 0; j < arr.length; j++) {
    localStorage.removeItem(arr[j]);
  }
}

export function getTokenExpirationMinutes(expiration: number): number {
  const now = moment();
  const exp = moment(expiration * 1000);
  const dur = moment.duration(exp.diff(now));
  return dur.asMinutes();
}

export function getExpirationDateMinutesFromNow(minutesFromNow: number): Date {
  const exp = moment().add(minutesFromNow, 'minutes');
  return exp.toDate();
}

export function isTokenExpired(expiration: number): boolean {
  return moment(expiration * 1000).isSameOrBefore(moment());
}

export function tokenCanBeRefreshed(refreshExpiresIn: number): boolean {
  return !isTokenExpired(refreshExpiresIn);
}

export function expirationPastThreshold(expiration: number, threshold: number): boolean {
  const now = moment();
  const exp = moment(expiration * 1000);
  return now.add(threshold, 'm') >= exp;
}

export function tokenReadyForRefresh(threshold: number = 2): boolean {
  const tokenExpireMeta = getTokenAndExpire();

  const expiresIn = Number(tokenExpireMeta[TOKEN_EXPIRES]);

  if (isTokenExpired(expiresIn)) {
    return true;
  }

  if (expirationPastThreshold(expiresIn, threshold)) {
    // console.log(
    //   `Ooops you are past the expiration threshold of ${threshold} minutes`,
    // );
    return true;
  }
  return false;
}
